import { getTokenHeader } from "@/helpers/commonHelper";
import {
  getAllowedMimeTypesByFieldType,
  isAllowedMimeType
} from "./fileMimeTypes";

export default {
  name: "FormPublisher",
  components: {
    FormFieldRenderer: () => import("./FormFieldRenderer.vue")
  },
  props: {
    form: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      show: true,
      formValid: false,
      formData: {},
      uploadedFiles: {},
      isSubmitting: false,
      submitSuccess: null,
      submitError: null,
      multiStepCurrent: {}
    };
  },
  watch: {
    form: {
      handler(newForm) {
        if (newForm && newForm.data?.fields) {
          this.initializeFormData();
        }
      },
      immediate: true
    }
  },
  methods: {
    getAllowedMimeTypesByFieldType(fieldType) {
      return getAllowedMimeTypesByFieldType(fieldType);
    },
    isAllowedMimeType(fieldType, mimeType) {
      return isAllowedMimeType(fieldType, mimeType);
    },
    getMimeTypeErrorMessage(fieldType) {
      if (fieldType === "image") {
        return "Bildformat nicht erlaubt. Bitte JPG, PNG, WEBP, GIF, SVG oder AVIF verwenden.";
      }
      return "Dateiformat nicht erlaubt.";
    },
    isFileField(fieldType) {
      return fieldType === "file" || fieldType === "image";
    },
    isFileLikeValue(value) {
      if (!value || typeof value !== "object") return false;
      return value instanceof File || value instanceof Blob;
    },
    isUploadedFileValue(value) {
      return !!(value && typeof value === "object" && value.url);
    },
    buildUploadPath(url) {
      if (!url) return "";
      try {
        return new URL(url).pathname || "";
      } catch (e) {
        return "";
      }
    },
    normalizeUploadedFile(uploaded, file, field) {
      const url = uploaded?.url || "";
      const title = uploaded?.title || file?.name || field?.label || "Datei";
      const mimeType = file?.type || "";

      return {
        id: uploaded?.id || uploaded?._id || "",
        title,
        name: file?.name || title,
        url,
        path: this.buildUploadPath(url),
        mimeType,
        size: Number(file?.size || 0),
        type: field?.type || "file"
      };
    },
    getFormIdForUpload() {
      return this.form?.data?.id || this.form?._id || this.form?.id || "";
    },
    getPrincipalIdForUpload() {
      return (
        this.form?.principal_id || this.$store.state.selectedPrincipal?.id || ""
      );
    },
    normalizeFileInputValue(value) {
      if (Array.isArray(value)) {
        return value[0] || "";
      }
      return value;
    },
    async uploadSingleFieldFile(field, fileValue) {
      const formId = this.getFormIdForUpload();
      if (!formId || !field?.id || !this.isFileLikeValue(fileValue)) {
        return null;
      }

      if (!this.isAllowedMimeType(field.type, fileValue?.type)) {
        const mimeError = this.getMimeTypeErrorMessage(field.type);
        this.submitError = mimeError;
        throw new Error(mimeError);
      }

      const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
      const apiUrl = env.form_builder_api_client_url;
      const formData = new FormData();
      formData.append("file", fileValue);

      const response = await this.$axios.post(
        `${apiUrl}api/forms/${formId}/upload`,
        formData,
        { headers: this.getTokenHeader() }
      );

      if (response?.data?.success !== true || !response?.data?.data) {
        throw new Error(
          response?.data?.message || "Datei konnte nicht hochgeladen werden."
        );
      }

      const uploaded = response.data.data;

      const normalized = this.normalizeUploadedFile(uploaded, fileValue, field);
      this.$set(this.uploadedFiles, field.id, normalized);
      return normalized;
    },
    async uploadSubmissionFiles(allFields) {
      const formId = this.getFormIdForUpload();
      if (!formId) return {};

      const uploadedValues = {};

      for (const field of allFields) {
        if (!this.isFileField(field.type)) continue;
        const rawValue = this.formData[field.id];
        const currentValue = this.normalizeFileInputValue(rawValue);
        if (!currentValue) continue;

        const cachedUpload = this.uploadedFiles[field.id];
        if (cachedUpload && this.isUploadedFileValue(cachedUpload)) {
          uploadedValues[field.id] = cachedUpload;
          continue;
        }

        if (this.isUploadedFileValue(currentValue)) {
          uploadedValues[field.id] = currentValue;
          continue;
        }

        if (!this.isFileLikeValue(currentValue)) continue;

        const normalized = await this.uploadSingleFieldFile(
          field,
          currentValue
        );
        if (normalized) {
          uploadedValues[field.id] = normalized;
        }
      }

      return uploadedValues;
    },
    goToMultiStep(fieldId, stepNumber, totalSteps) {
      const targetStep = Number(stepNumber);
      const maxSteps = Number(totalSteps);

      if (!fieldId || Number.isNaN(targetStep) || Number.isNaN(maxSteps)) {
        return;
      }

      if (targetStep >= 1 && targetStep <= maxSteps) {
        this.$set(this.multiStepCurrent, fieldId, targetStep);
      }
    },
    onFieldInput(fieldId, value) {
      const field = this.getAllFields(this.form?.data?.fields || []).find(
        currentField => currentField.id === fieldId
      );
      const normalizedValue = this.isFileField(field?.type)
        ? this.normalizeFileInputValue(value)
        : value;

      this.$set(this.formData, fieldId, normalizedValue);

      if (!this.isFileField(field?.type)) {
        return;
      }

      if (!normalizedValue) {
        this.$delete(this.uploadedFiles, fieldId);
        return;
      }

      if (this.isFileLikeValue(normalizedValue)) {
        // Neue Auswahl: ggf. alten Upload verwerfen, Upload erst beim Submit.
        this.$delete(this.uploadedFiles, fieldId);
      }

      if (this.isUploadedFileValue(normalizedValue)) {
        this.$set(this.uploadedFiles, fieldId, normalizedValue);
      }
    },
    isOptionField(fieldType) {
      return ["checkbox", "checkbox-image", "radio"].includes(fieldType);
    },
    getOptionValue(option) {
      const value = String(option?.value || "").trim();
      if (value) return value;
      return String(option?.label || "").trim();
    },
    getOptionIndexFromToken(token) {
      const normalized = String(token || "").trim();
      if (!normalized.startsWith("idx:")) return Number.MAX_VALUE;
      const index = Number(normalized.slice(4));
      return Number.isNaN(index) ? Number.MAX_VALUE : index;
    },
    resolveOptionBySelectedValue(field, selectedValue) {
      const normalized = String(selectedValue || "").trim();
      const options = field?.options || [];

      if (normalized.startsWith("idx:")) {
        const index = Number(normalized.slice(4));
        if (!Number.isNaN(index) && options[index]) return options[index];
      }
      return (
        options.find(option => this.getOptionValue(option) === normalized) ||
        null
      );
    },
    getOptionEntryBySelectedValue(field, selectedValue) {
      const normalizedValue = String(selectedValue || "").trim();
      const matchedOption = this.resolveOptionBySelectedValue(
        field,
        normalizedValue
      );
      const optionName = String(
        matchedOption?.label || normalizedValue || ""
      ).trim();
      const optionValue = String(
        matchedOption ? this.getOptionValue(matchedOption) : normalizedValue
      ).trim();
      if (!optionName || !optionValue) return null;
      return { [optionName]: optionValue };
    },
    normalizeSelectedValues(fieldType, currentValue) {
      if (fieldType === "radio") {
        const selected = String(currentValue || "").trim();
        return selected ? [selected] : [];
      }

      if (!Array.isArray(currentValue)) {
        const single = String(currentValue || "").trim();
        return single ? [single] : [];
      }

      return currentValue
        .map(value => String(value || "").trim())
        .filter(Boolean);
    },
    buildOptionEntries(field, currentValue) {
      const selectedValues = this.normalizeSelectedValues(
        field.type,
        currentValue
      );
      const sorted = selectedValues.sort((a, b) => {
        return (
          this.getOptionIndexFromToken(a) - this.getOptionIndexFromToken(b)
        );
      });

      return sorted
        .map(selectedValue =>
          this.getOptionEntryBySelectedValue(field, selectedValue)
        )
        .filter(Boolean);
    },
    getAllFields(fields) {
      const allFields = [];

      const collectFieldsRecursively = fieldList => {
        (fieldList || []).forEach(field => {
          if (field.type === "two-column" || field.type === "three-column") {
            (field.columns || []).forEach(col => {
              collectFieldsRecursively(col);
            });
            return;
          }

          if (field.type === "multi-step") {
            this.$set(this.multiStepCurrent, field.id, 1);
            (field.steps || []).forEach(step => {
              collectFieldsRecursively(step.fields || []);
            });
            return;
          }

          allFields.push(field);
        });
      };

      collectFieldsRecursively(fields || []);
      return allFields;
    },
    buildSubmitPayload(uploadedFileValues = {}) {
      const payload = JSON.parse(JSON.stringify(this.formData || {}));
      const allFields = this.getAllFields(this.form?.data?.fields || []);

      Object.keys(this.uploadedFiles || {}).forEach(fieldId => {
        const cachedValue = this.uploadedFiles[fieldId];
        if (this.isUploadedFileValue(cachedValue)) {
          payload[fieldId] = cachedValue;
        }
      });

      Object.keys(uploadedFileValues || {}).forEach(fieldId => {
        payload[fieldId] = uploadedFileValues[fieldId];
      });

      allFields.forEach(field => {
        if (
          this.isFileField(field.type) &&
          this.isFileLikeValue(payload[field.id])
        ) {
          // Sicherheitsnetz: File-Objekte duerfen nicht roh serialisiert werden.
          payload[field.id] = "";
        }
        if (!this.isOptionField(field.type)) return;
        payload[field.id] = this.buildOptionEntries(field, payload[field.id]);
      });

      allFields.forEach(field => {
        if (!this.isFileField(field.type)) return;
        const value = payload[field.id];
        // Gewuenschtes Submission-Format: { "feldId": "url" }
        if (this.isUploadedFileValue(value)) {
          payload[field.id] = value.url || "";
          return;
        }
        if (typeof value === "string") {
          payload[field.id] = value;
        }
      });

      return payload;
    },
    initializeFormData() {
      this.submitSuccess = null;
      this.submitError = null;
      this.formData = {};
      this.uploadedFiles = {};
      this.multiStepCurrent = {};

      const fields = this.form.data?.fields || [];
      const allFields = this.getAllFields(fields);

      allFields.forEach(field => {
        if (field.type === "checkbox" || field.type === "checkbox-image") {
          this.formData[field.id] = [];
        } else if (
          field.type === "section" ||
          field.type === "submit" ||
          field.type === "html" ||
          field.type === "link" ||
          field.type === "hidden"
        ) {
          // Diese Feldtypen speichern keine Daten
        } else {
          this.formData[field.id] = "";
        }
      });
    },
    async submitForm() {
      if (this.isSubmitting) return;

      // Validiere das Formular
      if (!this.$refs.previewForm.validate()) {
        this.submitError = "Bitte füllen Sie alle erforderlichen Felder aus.";
        return;
      }

      try {
        this.isSubmitting = true;
        // Hol die Umgebungsvariablen
        const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
        const apiUrl = env.form_builder_api_client_url;
        const principalId = this.getPrincipalIdForUpload();
        const formId = this.form.data?.id || this.form._id;
        const allFields = this.getAllFields(this.form?.data?.fields || []);
        const uploadedFileValues = await this.uploadSubmissionFiles(allFields);
        const payload = this.buildSubmitPayload(uploadedFileValues);

        // Schicke das Formular an den eo-forms Service
        const response = await this.$axios.post(
          `${apiUrl}api/forms/${principalId}/${formId}/submit`,
          { data: payload },
          { headers: this.getTokenHeader() }
        );

        if (response.data.success) {
          this.submitSuccess = "Formular erfolgreich versendet!";
          this.$emit("submit", payload);
        } else {
          this.submitError =
            response.data.message || "Fehler beim Versenden des Formulars";
        }
      } catch (error) {
        console.error("Fehler beim Versenden des Formulars:", error);
        this.submitError =
          error.response?.data?.message ||
          "Fehler beim Versenden des Formulars";
      } finally {
        this.isSubmitting = false;
      }
    },
    cancel() {
      this.show = false;
      this.$emit("cancel");
    },
    getTokenHeader() {
      return getTokenHeader();
    }
  }
};
